import { Typography } from '@material-ui/core'
import { RGBColor, color } from 'd3-color'
import { scaleLinear, scaleThreshold } from 'd3-scale'
import DeckGL, { GeoJsonLayer, RGBAColor } from 'deck.gl'
import { range, round } from 'lodash'
import 'mapbox-gl/dist/mapbox-gl.css'
import React, { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMapGL, { ViewState } from 'react-map-gl'

import { useViewport } from 'src/layout/hooks'
import SETTINGS from 'src/settings'

import { IFeature } from '../types'
import { formatNumber } from '../utils'

import { useGraphContext } from './GraphWrapper/GraphWrappers'

/*
 * Adapted from https://github.com/uber/deck.gl/blob/7.0-release/examples/website/geojson/app.js
 */

const OPACITY = 80

const getBinaryColorScale = (scale: number[], colors: string[]) =>
  (scaleThreshold().domain([scale[scale.length - 1]]) as any).range(colors)

const getColorScale = (scale: number[], colors: string[]) => (scaleLinear().domain(scale) as any).range(colors)

const getFillColor = (num: number, scale: number[], colors: string[], binaryScale?: boolean) => {
  const rgb = binaryScale ? getBinaryColorScale(scale, colors)(num) : getColorScale(scale, colors)(num)
  const { r, g, b } = color(rgb) as RGBColor
  return [r, g, b, OPACITY] as RGBAColor
}

function getLayers(data: any, onHover: any, scale: number[], colors: string[], binaryScale?: boolean) {
  return [
    new GeoJsonLayer<IFeature>({
      id: 'geojson-layer',
      data,
      pickable: true,
      stroked: true,
      filled: true,
      extruded: false,
      lineWidthScale: 10,
      lineWidthMinPixels: 1,
      getFillColor: (f: IFeature) => getFillColor(f.properties.value, scale, colors, binaryScale),
      getElevation: 100,
      getRadius: 100,
      getLineColor: [255, 255, 255],
      getLineWidth: 1,
      onHover,
    }),
  ]
}

export function useTooltip(render: (properties: any, legendMap?: string) => ReactNode) {
  const [hoveredObject, setHoveredObject] = useState(null)
  const [x, setX] = useState(null)
  const [y, setY] = useState(null)

  function onHover(e: any) {
    setHoveredObject(e.object)
    // small offset so that mouse cursor is not directly on the popup.
    // otherwise popup would just disappear immediately again
    setX(e.x + 5)
    setY(e.y + 5)
  }

  function renderTooltip() {
    if (!hoveredObject) {
      return null
    }

    return (
      <div
        style={{
          position: 'absolute',
          top: `${y || 0}px`,
          left: `${x || 0}px`,
          backgroundColor: 'white',
          width: 200,
          padding: 8,
          zIndex: 1,
          opacity: 0.7,
        }}
      >
        {render((hoveredObject as any).properties)}
      </div>
    )
  }
  return [onHover, renderTooltip]
}

const styles = {
  container: (width: number) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    width,
  }),
  legendContainer: {
    borderRadius: '4px',
    backgroundColor: 'white',
    minWidth: '200px',
    width: '25%',
    marginRight: '20px',
    marginTop: '20px',
    padding: 8,
    zIndex: 1,
  },
  colorRange: { display: 'flex', flexDirection: 'row' },
  legendRange: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  binaryLegendRange: {
    display: 'flex',
    flexDirection: 'row',
  },
  binaryScaleWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  legendColor: (backgroundColor: string) => ({
    height: '20px',
    width: '10%',
    backgroundColor,
    opacity: 0.5,
  }),
  binaryLegendColor: (backgroundColor: string) => ({
    height: '20px',
    width: '10%',
    backgroundColor,
    opacity: 0.5,
    marginRight: '10px',
    marginBottom: '10px',
  }),
}

interface IProps {
  binaryScale?: boolean
  colorScale: string[]
  data: any
  height: number
  legend?: string
  legendFormatter?: (num: number) => string
  onHover: (e: any) => void
  renderTooltip: any
  scale: number[]
  title?: string
  width: number
}

function Choropleth({
  width,
  height,
  data,
  onHover,
  scale,
  colorScale,
  renderTooltip,
  legend,
  binaryScale,
  legendFormatter = (num) => formatNumber(round(num, 2)),
}: IProps) {
  const { deckGL } = useGraphContext()

  const agencyViewport = useViewport()
  const initialViewport = {
    latitude: agencyViewport.center.lat,
    longitude: agencyViewport.center.lng,
    zoom: agencyViewport.zoom,
    maxZoom: 20,
    pitch: 0,
    bearing: 0,
    padding: {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    },
  }

  const { t } = useTranslation()

  const [viewport, setViewPort] = useState<ViewState>(initialViewport)

  const handleViewPortChange = ({ viewState }: { viewState: ViewState }) => setViewPort(viewState)

  const onLoad = (evt: any) => {
    const map = evt.target
    const { layers = [] } = (map && map.getStyle()) || { layers: [] }
    layers.forEach((layer: any) => {
      if (layer.layout && layer.layout['text-field']) {
        map.setLayoutProperty(layer.id, 'text-field', ['get', t('language.map')])
      }
    })
  }

  if (!width || !height) {
    return null
  }

  const min = scale[0]
  const max = scale[scale.length - 1]
  const step = (max - min) / 10
  const legendRange = range(min, max + step, step)

  function renderLegend() {
    return (
      <div style={styles.container(width)} ref={deckGL.mapLegendRef}>
        <div style={styles.legendContainer}>
          <Typography variant="body1">
            <span style={{ fontSize: '1.05em' }}>{legend}</span>
          </Typography>
          <br />
          {binaryScale ? (
            <div style={styles.binaryScaleWrapper as any}>
              {scale.map(
                (value: number): ReactNode => (
                  <div key={`wrap-${value}`} style={styles.binaryLegendRange as any}>
                    <div style={styles.binaryLegendColor(getBinaryColorScale(scale, colorScale)(value))} />
                    <Typography variant="body1" key={value}>
                      {legendFormatter(value)}
                    </Typography>
                  </div>
                )
              )}
            </div>
          ) : (
            <>
              <div style={styles.colorRange as any}>
                {legendRange.map(
                  (value: number): ReactNode => (
                    <div key={value} style={styles.legendColor(getColorScale(scale, colorScale)(value))} />
                  )
                )}
              </div>
              <div style={styles.legendRange as any}>
                {legendRange.map(
                  (value: number, index: number): ReactNode =>
                    index % 5 === 0 && (
                      <Typography variant="body1" key={value}>
                        {legendFormatter(value)}
                      </Typography>
                    )
                )}
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <DeckGL
      ref={deckGL.deckRef}
      initialViewState={initialViewport}
      viewState={viewport}
      onViewStateChange={handleViewPortChange}
      layers={getLayers(data, onHover, scale, colorScale, binaryScale)}
      controller={true}
      glOptions={{
        preserveDrawingBuffer: true,
      }}
    >
      <ReactMapGL
        ref={deckGL.mapGlRef}
        preserveDrawingBuffer={true}
        // Token
        mapboxApiAccessToken={SETTINGS.app.mapbox_token}
        // Size
        width={width}
        height={height}
        onLoad={onLoad}
      />
      {/*
        do not move the order of components here, otherwise the legend will not have a fixed position
        => https://polyconseil.atlassian.net/browse/SMP-2296
      */}
      {renderLegend}
      {renderTooltip}
    </DeckGL>
  )
}

export default Choropleth
